import { Button, ButtonVariant } from "components/Form/Button";
import { Input } from "components/Form/Input";
import { Layout } from "components/Layout";
import { Field } from "components/Form/Field";
import { Modal } from "components/Modal";
import { apiSignup } from "lib/api";
import { Dispatch, SetStateAction, memo, useCallback, useState } from "react";
import { Storage, StorageKey } from "lib/storage";
import { SubmitHandler, useForm } from "react-hook-form";
import { SignupScreens } from "types";
import { Select } from "components/Form/Select";
import { PSC } from "components/PCS";
import { toast } from "react-toastify";

// Validation helper
const nameValidation = {
  required: true,
  minLength: 2,
  pattern: {
    value: /^[A-Za-z\s'-]+$/,
    message: "Only letters, spaces, hyphens and apostrophes allowed",
  },
};

const initialValidation = {
  required: true,
  pattern: {
    value: /^[A-Za-z]$/,
    message: "Only letters allowed",
  },
};

const handleNameKeyDown = (e: React.KeyboardEvent) => {
  if (
    !/[A-Za-z\s'-]/.test(e.key) &&
    !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
  ) {
    e.preventDefault();
  }
};

const handleInitialKeyDown = (e: React.KeyboardEvent) => {
  if (
    !/[A-Za-z]/.test(e.key) &&
    !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
  ) {
    e.preventDefault();
  }
};

enum Gender {
  Male = "male",
  Female = "female",
  Other = "other",
  "Prefer not to say" = "unknown",
}

enum Ethnicity {
  Black = "black",
  White = "white",
  "Coloured (South Africa)" = "coloured",
  "Middle Eastern" = "middle_eastern",
  "North African" = "north_african",
  Hispanic = "hispanic",
  "South Asian" = "south_asian",
  "Central Asian" = "central_asian",
  "East Asian" = "east_asian",
  "Southeast Asian" = "southeast_asian",
  "Pacific Islander" = "pacific_islander",
  "Māori" = "maori",
  "Aboriginal Australian" = "aboriginal_australian",
  "Native American" = "native_american",
  "Indigenous South American" = "indigenous_south_american",
  Other = "other",
  "Prefer not to say" = "unknown",
}

type FormValue = {
  firstname: string;
  lastname: string;
  mothers_name: string;
  fathers_name: string;
  date_of_birth: string;
  gender: Gender;
  ethnicity: Array<Ethnicity>;
  agreement: boolean;
  legal_status: "adult";
};

export const Information = memo(function SignUp(props: {
  setStep: Dispatch<SetStateAction<SignupScreens>>;
}) {
  const [loading, setLoading] = useState(false);
  const [showBackWarning, setShowBackWarning] = useState(false);
  const [showMinorPopup, setShowMinorPopup] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isValid, errors },
    watch,
    trigger,
  } = useForm<FormValue>({
    mode: "onChange",
  });

  const watchLegalStatus = watch("legal_status");
  const watchGender = watch("gender");
  const watchEthnicity = watch("ethnicity");

  const onSubmit = useCallback<SubmitHandler<FormValue>>(
    async (data) => {
      if (data.legal_status !== "adult") {
        setShowMinorPopup(true);
        return;
      }

      if (isValid) {
        setLoading(true);
        try {
          const newToken = await apiSignup({
            ...data,
            ethnicity: JSON.stringify(data.ethnicity),
          });

          // Update token with registered status
          // But don't navigate away yet - user still needs to verify email and set password
          Storage.set(StorageKey.TOKEN, newToken);

          // Move to email confirmation step
          toast.success("Information saved! Let's confirm your email.");
          props.setStep(SignupScreens.EmailConfirmation);
        } catch (err) {
          toast.error("Something went wrong, please try again later.");
        } finally {
          setLoading(false);
        }
      }
    },
    [isValid, props]
  );

  const handleConfirmBack = useCallback(() => {
    // Clear token when going back to restart signup
    Storage.remove(StorageKey.TOKEN);
    setShowBackWarning(false);
    props.setStep(SignupScreens.Email);
  }, [props]);

  return (
    <form className="contents" onSubmit={handleSubmit(onSubmit)}>
      <Layout
        className="space-y-4 md:space-y-6"
        toolbarClassName="bg-white border-t border-f4f7ff shadow-lg"
        toolbar={
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={!isValid || loading}
              className="flex-1 py-3 text-14 md:text-16 md:py-4.5"
            >
              {loading ? "Loading ..." : "Continue"}
            </Button>
            <Button
              variant={ButtonVariant.FLAT}
              onClick={() => setShowBackWarning(true)}
              className="py-3 text-14 md:text-16 md:py-4.5"
            >
              Back
            </Button>
          </div>
        }
      >
        <div className="space-y-2 text-center">
          <h1 className="text-20 md:text-24 font-semibold leading-1.2">
            Let&apos;s get you signed up!
          </h1>

          <div className="text-14 md:text-16 text-858494">
            <p>
              To match the previously captured data, please provide us
              information below
            </p>
          </div>
        </div>

        <div className="text-left w-full space-y-4 md:space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <Field label="First name" as="label" className="compact-field">
              <Input
                disabled={loading}
                id="firstName"
                {...register("firstname", nameValidation)}
                onKeyDown={handleNameKeyDown}
                className="h-10 md:h-12"
                placeholder="Enter first name"
              />
            </Field>

            <Field label="Last name" as="label" className="compact-field">
              <Input
                disabled={loading}
                id="lastName"
                {...register("lastname", nameValidation)}
                onKeyDown={handleNameKeyDown}
                className="h-10 md:h-12"
                placeholder="Enter last name"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-5">
            <Field
              label="Mother's Initial"
              as="label"
              className="compact-field"
            >
              <Input
                disabled={loading}
                id="motherName"
                maxLength={1}
                {...register("mothers_name", initialValidation)}
                onKeyDown={handleInitialKeyDown}
                className="h-10 md:h-12 text-center font-semibold"
                placeholder="M"
              />
            </Field>

            <Field
              label="Father's Initial"
              as="label"
              className="compact-field"
            >
              <Input
                disabled={loading}
                id="fatherName"
                maxLength={1}
                {...register("fathers_name", initialValidation)}
                onKeyDown={handleInitialKeyDown}
                className="h-10 md:h-12 text-center font-semibold"
                placeholder="F"
              />
            </Field>
          </div>

          <Field
            label="Date of Birth"
            as="label"
            className="compact-field"
            description={errors.date_of_birth?.message || "Month and year only"}
            descriptionClassName={
              errors.date_of_birth?.message ? "text-fd380f text-12" : "text-12"
            }
          >
            <Input
              disabled={loading}
              id="dob"
              type="month"
              {...register("date_of_birth", {
                required: true,
                validate: (value) => {
                  const [year, month] = value.split("-").map(Number);
                  const today = new Date();
                  const age =
                    today.getFullYear() -
                    year -
                    (today.getMonth() + 1 < month ? 1 : 0);
                  if (age < 18) return "You must be 18 or older to sign up";
                  if (age > 100) return "Age cannot exceed 100 years";
                  return true;
                },
              })}
              className="w-full h-10 md:h-12"
              onChange={async (e) => {
                setValue("date_of_birth", e.target.value);
                await trigger("date_of_birth");
              }}
            />
          </Field>

          <div className="p-3 md:p-4 text-12 md:text-14 text-858494 border rounded-lg border-f4f7ff bg-f4f7ff/30">
            In order to ensure that our biometric models are trained on a non
            discriminatory and diverse data set, Tools for Humanity Corp and
            GmbH (joint-controllers) would like to assign your ethnicity to your
            image data. You can withdraw your consent at any time in the app.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <Field label="Gender" className="compact-field">
              <Select
                options={Gender}
                value={watchGender}
                renderValue={(v) =>
                  Object.entries(Gender).find((i) => i[1] === v)?.[0]
                }
                onChange={(v) => {
                  setValue("gender", v as Gender, {
                    shouldValidate: true,
                  });
                }}
                className="h-10 md:h-12"
              />
            </Field>

            <Field
              label="Ethnicity"
              className="compact-field"
              description="Select up to 3"
            >
              <Select
                multiple
                options={Ethnicity}
                value={watchEthnicity}
                renderValue={(v) => {
                  if (!v || v.length === 0) return "Select ethnicity";
                  return v
                    .map(
                      (i) =>
                        Object.entries(Ethnicity).find((j) => j[1] === i)?.[0]
                    )
                    .join(", ");
                }}
                onChange={(v) => {
                  if (v.length <= 3) {
                    setValue("ethnicity", v as Ethnicity[], {
                      shouldValidate: true,
                    });
                  }
                }}
                className="h-10 md:h-12"
              />
            </Field>
          </div>

          <Field label="Legal Status" className="compact-field">
            <div className="flex gap-4 md:gap-5">
              <label className="flex items-center gap-2 text-14 md:text-16">
                <input
                  type="checkbox"
                  checked={watchLegalStatus === "adult"}
                  onChange={() =>
                    setValue("legal_status", "adult", { shouldValidate: true })
                  }
                  className="w-4 h-4"
                />
                Adult
              </label>

              <label className="flex items-center gap-2 text-14 md:text-16">
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => setShowMinorPopup(true)}
                  className="w-4 h-4"
                />
                Minor
              </label>
            </div>
          </Field>

          <PSC
            onDisplayed={() => register("agreement", { required: true })}
            onValid={() =>
              setValue("agreement", true, { shouldValidate: true })
            }
            onInvalid={() =>
              setValue("agreement", false, { shouldValidate: true })
            }
            onError={() =>
              setValue("agreement", true, { shouldValidate: true })
            }
          />
        </div>

        {/* Scroll indicator for mobile */}
        <div className="h-20 md:h-24" aria-hidden="true">
          {/* This empty space ensures content isn't hidden behind the toolbar */}
        </div>
      </Layout>

      <Modal show={showMinorPopup} onClose={() => setShowMinorPopup(false)}>
        <div className="space-y-6 text-center">
          <div className="space-y-2">
            <h2 className="text-24 font-semibold">Adults only</h2>
            <p className="text-858494">
              Please confirm that you are an adult before proceeding. If you are
              a minor, your legal guardian can create a profile for you from
              their account and provide consent for your participation.
            </p>
          </div>
          <Button onClick={() => setShowMinorPopup(false)} className="w-full">
            OK
          </Button>
        </div>
      </Modal>

      <Modal show={showBackWarning} onClose={() => setShowBackWarning(false)}>
        <div className="space-y-6 text-center">
          <div className="space-y-2">
            <h2 className="text-24 font-semibold">Lose your progress?</h2>
            <p className="text-858494">
              Going back will clear all the information you've entered. You'll
              need to start the signup process from the beginning.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setShowBackWarning(false)}
              className="flex-1"
            >
              Stay Here
            </Button>
            <Button
              onClick={handleConfirmBack}
              variant={ButtonVariant.FLAT_BORDER}
              className="flex-1"
            >
              Go Back
            </Button>
          </div>
        </div>
      </Modal>
    </form>
  );
});
